import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { HttpErrorsService } from "@sharedV11/services/alerts/errors/http-errors.service";
import { SnackbarInfoService } from "@sharedV11/services/alerts/snackbar-info/snackbar-info.service";
import { ConectarApiService } from "@sharedV11/services/api/connection/conectar-api.service";
import { PanelService } from "@sharedV11/services/panel/panel.service";
import { Translator } from "@sharedV11/services/translator/translator.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-edit-alojamiento-tx",
  templateUrl: "./edit-alojamiento-tx.component.html",
  styleUrls: ["./edit-alojamiento-tx.component.scss"],
})
export class EditAlojamientoTxComponent implements OnInit, OnDestroy {
  @Input() pkId: string;
  @Input() mode: string;
  @Input() modificar: boolean;

  loading = true;
  translation: any;
  editAccommodationForm: FormGroup;
  private destroy$ = new Subject<void>();
  private originalValues: any;

  private static readonly FORBIDDEN_SEQUENCES = [
    ";",
    "/*",
    "*/",
    "@@",
    "!!",
    "--",
    "==",
    "//",
    "\\\\",
  ];

  constructor(
    private fb: FormBuilder,
    private translator: Translator,
    private httpErrorService: HttpErrorsService,
    private snackvar_service: SnackbarInfoService,
    private panelService: PanelService,
    private apiService: ConectarApiService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.translation = this.translator.GetTranslations();
    this.initializeForm();
    this.registerDirtyWatcher();
    this.loadFormData(this.pkId, this.mode);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private static forbiddenCharsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || typeof control.value !== "string") return null;
      const value = control.value;
      const found = EditAlojamientoTxComponent.FORBIDDEN_SEQUENCES.find((seq) =>
        value.includes(seq)
      );
      return found ? { forbiddenChars: { value: found } } : null;
    };
  }

  static sanitizeForbiddenSequences(value: string): string {
    let sanitized = (value ?? "").toString();
    EditAlojamientoTxComponent.FORBIDDEN_SEQUENCES.forEach((seq) => {
      sanitized = sanitized.split(seq).join("");
    });
    return sanitized;
  }

  onInputForbiddenSequences(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement;
    const value = input.value ?? "";
    const sanitized = EditAlojamientoTxComponent.sanitizeForbiddenSequences(value);
    if (sanitized !== value) {
      const control = this.editAccommodationForm.get(controlName);
      if (control) {
        control.setValue(sanitized, { emitEvent: true });
        input.value = sanitized;
      }
    }
  }

  initializeForm() {
    const noForbiddenSequences = EditAlojamientoTxComponent.forbiddenCharsValidator();
    this.editAccommodationForm = this.fb.group({
      pkId: [0],
      CodAlojamientoTX: [
        "",
        [Validators.required, Validators.maxLength(50), noForbiddenSequences],
      ],
      DescriptivoTX: [
        "",
        [Validators.required, Validators.maxLength(255), noForbiddenSequences],
      ],
      DescriptivoEspTX: [
        "",
        [Validators.required, Validators.maxLength(255), noForbiddenSequences],
      ],
    });
  }

  private registerDirtyWatcher() {
    if (!this.editAccommodationForm) return;
    this.editAccommodationForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.panelService.setDiscardChanges(false));
  }

  loadFormData(pkId: string, mode: string) {
    if (pkId && mode !== "I") {
      const paginator = {
        page: 1,
        pagelements: 1,
        orderby: "pkId",
        ordertype: "ASC",
      };
      this.apiService
        .getTaxonomyAccommodationAsync(paginator, undefined, pkId)
        .then((data) => {
          const list = data?.DatosResult?.Lista ?? [];
          if (list.length) {
            const item = list[0];
            const isDuplicate = mode === "D";
            this.editAccommodationForm.patchValue({
              pkId: isDuplicate ? 0 : +(item.pkId || 0),
              CodAlojamientoTX: EditAlojamientoTxComponent.sanitizeForbiddenSequences(
                (item.CodAlojamientoTX ?? item.Codigo ?? "").toString()
              ),
              DescriptivoTX: EditAlojamientoTxComponent.sanitizeForbiddenSequences(
                (item.DescriptivoTX ?? item.Descriptivo ?? "").toString()
              ),
              DescriptivoEspTX: EditAlojamientoTxComponent.sanitizeForbiddenSequences(
                (item.DescriptivoEspTX ?? item.DescriptivoEsp ?? "").toString()
              ),
            });
            if (isDuplicate) this.mode = "I";
          }
          this.originalValues = { ...this.editAccommodationForm.value };
          this.panelService.setDiscardChanges(true);
          this.loading = false;
        })
        .catch((error) => {
          this.httpErrorService.identificarErrores(error);
          this.loading = false;
        });
    } else {
      this.editAccommodationForm.patchValue({
        pkId: 0,
        CodAlojamientoTX: "",
        DescriptivoTX: "",
        DescriptivoEspTX: "",
      });
      this.originalValues = { ...this.editAccommodationForm.value };
      this.loading = false;
      this.panelService.setDiscardChanges(true);
    }
  }

  discard() {
    if (this.editAccommodationForm && this.originalValues) {
      this.editAccommodationForm.reset(this.originalValues);
      this.panelService.setDiscardChanges(true);
    }
  }

  submit() {
    this.editAccommodationForm.markAllAsTouched();
    if (this.editAccommodationForm.invalid) return;

    const value = this.editAccommodationForm.value;
    const payload = {
      pkId:
        this.mode === "U"
          ? value.pkId || 0
          : 0,
      CodAlojamientoTX: EditAlojamientoTxComponent.sanitizeForbiddenSequences(
        value.CodAlojamientoTX ?? ""
      ),
      DescriptivoTX: EditAlojamientoTxComponent.sanitizeForbiddenSequences(
        value.DescriptivoTX ?? ""
      ),
      DescriptivoEspTX: EditAlojamientoTxComponent.sanitizeForbiddenSequences(
        value.DescriptivoEspTX ?? ""
      ),
    };

    this.loading = true;
    this.apiService
      .upsertTaxonomyAccommodationAsync(payload)
      .then((response) => {
        if (response?.Mensajes?.length) {
          response.Mensajes.forEach((m) => {
            const code = String(m.CodigoMensaje);
            const txKey = `CodErr_TXALOJ_UPSERT_${code}`;
            const translated =
              this.translation?.[txKey] ||
              this.translation?.[`CodErr_${code}`] ||
              m.DescripcionMensaje;
            this.snackvar_service.openSnackBar(
              translated,
              "",
              "red-snackbar"
            );
          });
        } else {
          this.snackvar_service.openSnackBar(
            this.translation["MensajeSuccessSnackBar"] ||
              this.translation["GuardadoCorrectamente"] ||
              "Guardado correctamente",
            "",
            "green-snackbar"
          );
        }
        this.closePanel();
      })
      .catch((error) => {
        this.httpErrorService.identificarErrores(error);
        this.loading = false;
      });
  }

  getErrorMessage(field: string) {
    const control = this.editAccommodationForm.get(field);
    if (!control?.errors) return "";
    if (control.hasError("required"))
      return this.translation?.CampoRequerido || "Campo requerido";
    if (control.hasError("maxlength")) {
      const max = control.getError("maxlength");
      return (
        (this.translation?.LongitudMaxima || "Longitud máxima") +
        ": " +
        (max?.requiredLength || "")
      );
    }
    if (control.hasError("forbiddenChars")) {
      const err = control.getError("forbiddenChars");
      const seq = err?.value ?? "";
      const msg =
        this.translation?.CaracteresNoPermitidos ||
        "Caracteres o combinación no permitida por el backend";
      return seq ? `${msg}: «${seq}»` : msg;
    }
    return "Error";
  }

  validateFields(field: string) {
    const control = this.editAccommodationForm.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  closePanel() {
    this.loading = false;
    this.panelService.setSavePanel(null);
    this.panelService.setDiscardChanges(true);
    this.panelService.setClosePanel(null);
  }
}
